import { api } from '@smena/ui';

export interface SessionListItem {
  id: string;
  sid: string;
  channelRef: string;
  humanTookOver: boolean;
  guestName: string | null;
  guestPhone: string | null;
  messagesCount: number;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SessionThread {
  session: {
    id: string;
    sid: string;
    channelRef: string;
    humanTookOver: boolean;
    guestName: string | null;
    guestPhone: string | null;
    bookingId: string | null;
    createdAt: string;
  };
  messages: Array<{ id: number; role: string; content: string; channelRef: string | null; ts: string }>;
}

export interface EscalationItem {
  id: string;
  sid: string | null;
  ts: string;
  reason: string;
  text: string;
  suggestedReply: string | null;
  status: string;
  handledByEmail: string | null;
  handledAt: string | null;
}

export interface DraftItem {
  id: string;
  sid: string | null;
  intent: string;
  replyText: string;
  createdAt: string;
  handledAt: string | null;
}

export interface PendingActionItem {
  id: string;
  sid: string;
  toolName: string;
  status: string;
  expiresAt: string;
  decidedByEmail: string | null;
  decidedAt: string | null;
  reason: string | null;
  createdAt: string;
}

export interface IngestEventItem {
  id: number;
  ts: string;
  source: string;
  action: string;
  propertyTitle: string | null;
  bookingGuest: string | null;
  detail: Record<string, unknown>;
}

export interface AuditLogItem {
  id: number;
  ts: string;
  accountId: string | null;
  accountSlug: string | null;
  actor: string;
  action: string;
  payload: Record<string, unknown>;
}

export interface AdminAuditItem {
  id: number;
  ts: string;
  adminUserId: string | null;
  adminUserEmail: string | null;
  action: string;
  targetAccountId: string | null;
  targetAccountSlug: string | null;
  payload: Record<string, unknown>;
  ip: string | null;
}

const items = <T,>(p: Promise<{ items: T[] }>): Promise<T[]> => p.then((r) => r.items);

export function fetchSessions(accountId: string): Promise<SessionListItem[]> {
  return items(api<{ items: SessionListItem[] }>(`/api/admin/accounts/${accountId}/sessions`));
}

/** ВНИМАНИЕ: открытие нити пишется в admin_audit на сервере - это просмотр чужих ПДн. */
export function fetchThread(accountId: string, sid: string): Promise<SessionThread> {
  return api<SessionThread>(
    `/api/admin/accounts/${accountId}/sessions/${encodeURIComponent(sid)}/messages`,
  );
}

export function fetchEscalations(accountId: string, status?: string): Promise<EscalationItem[]> {
  const qs = status ? `?status=${status}` : '';
  return items(api<{ items: EscalationItem[] }>(`/api/admin/accounts/${accountId}/escalations${qs}`));
}

export function fetchDrafts(accountId: string): Promise<DraftItem[]> {
  return items(api<{ items: DraftItem[] }>(`/api/admin/accounts/${accountId}/drafts`));
}

export function fetchPendingActions(accountId: string): Promise<PendingActionItem[]> {
  return items(api<{ items: PendingActionItem[] }>(`/api/admin/accounts/${accountId}/pending-actions`));
}

export function fetchIngestEvents(accountId: string): Promise<IngestEventItem[]> {
  return items(api<{ items: IngestEventItem[] }>(`/api/admin/accounts/${accountId}/ingest-events`));
}

export function fetchAuditLog(filter: {
  account?: string;
  actor?: string;
  action?: string;
  limit?: number;
}): Promise<AuditLogItem[]> {
  return items(api<{ items: AuditLogItem[] }>(`/api/admin/audit${qs(filter)}`));
}

export function fetchAuditActions(): Promise<string[]> {
  return items(api<{ items: string[] }>('/api/admin/audit/actions'));
}

export function fetchAdminAudit(filter: {
  account?: string;
  action?: string;
  limit?: number;
}): Promise<AdminAuditItem[]> {
  return items(api<{ items: AdminAuditItem[] }>(`/api/admin/audit/self${qs(filter)}`));
}

function qs(filter: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}
